import {
  MANUSCRIPT_CONTENT_LIMITS,
  MANUSCRIPT_CONTENT_VERSION,
  normalizeManuscriptContent,
  plainTextFromManuscriptContent,
} from "./childrensBookManuscriptContent";

const SPLITTABLE_BLOCK_TYPES = new Set(["paragraph", "blockquote"]);
const UNSPLITTABLE_BLOCK_TYPES = new Set(["heading", "list_item"]);

function failure(code, error) {
  return { ok: false, code, error };
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function wordsFromText(value) {
  const text = String(value ?? "").trim();
  return text ? text.split(/\s+/).filter(Boolean) : [];
}

function blockText(block) {
  return safeArray(block?.runs)
    .map((run) => (typeof run?.text === "string" ? run.text : ""))
    .join("");
}

function wordMatches(value) {
  return Array.from(String(value ?? "").matchAll(/\S+/g));
}

function continuationBlockId(sourceId, fragmentNumber) {
  const requested =
    String(
      sourceId ||
        "block"
    );

  const existing =
    requested.match(
      /^(.*)-part-(\d+)$/
    );

  const existingFragment =
    existing &&
    existing[1] &&
    Number(
      existing[2]
    ) >= 2
      ? Number(
          existing[2]
        )
      : 1;

  const requestedBase =
    existing &&
    existing[1] &&
    existingFragment >= 2
      ? existing[1]
      : requested;

  const targetFragment =
    existingFragment +
    Number(
      fragmentNumber ||
        1
    ) -
    1;

  const suffix =
    `-part-${targetFragment}`;

  const maximum =
    MANUSCRIPT_CONTENT_LIMITS
      .maximumBlockIdCharacters;

  const base =
    requestedBase.slice(
      0,
      Math.max(
        1,
        maximum -
          suffix.length
      )
    );

  return `${base}${suffix}`;
}

function sourceLayout(value) {
  const normalized = normalizeManuscriptContent(value);
  if (!normalized.ok || !normalized.content) {
    return failure(
      "source_manuscript_invalid",
      normalized.errors[0] || "The structured manuscript content is invalid."
    );
  }

  const content = normalized.content;
  const descriptors = [];
  const forcedBoundaries = new Set();
  const blockEndBoundaries = new Set();
  const words = [];
  let wordCursor = 0;
  let emptyForcedSection = false;

  content.blocks.forEach((block, blockIndex) => {
    if (block.type === "page_break") {
      if (forcedBoundaries.has(wordCursor)) {
        emptyForcedSection = true;
      }
      descriptors.push({
        block,
        blockIndex,
        type: block.type,
        wordStart: wordCursor,
        wordEnd: wordCursor,
        wordCount: 0,
        pageBreak: true,
        splittable: false,
        unsplittable: false,
      });
      forcedBoundaries.add(wordCursor);
      return;
    }

    const text = blockText(block);
    const matches = wordMatches(text);
    const blockWords = matches.map((match) => match[0]);
    const wordStart = wordCursor;
    wordCursor += blockWords.length;
    words.push(...blockWords);

    descriptors.push({
      block,
      blockIndex,
      type: block.type,
      text,
      matches,
      wordStart,
      wordEnd: wordCursor,
      wordCount: blockWords.length,
      pageBreak: false,
      splittable: SPLITTABLE_BLOCK_TYPES.has(block.type),
      unsplittable: UNSPLITTABLE_BLOCK_TYPES.has(block.type),
    });
    blockEndBoundaries.add(wordCursor);
  });

  if (emptyForcedSection) {
    return failure(
      "unsupported_page_break_position",
      "Consecutive or empty page breaks cannot be divided safely."
    );
  }

  if (forcedBoundaries.has(0) && wordCursor > 0) {
    return failure(
      "unsupported_page_break_position",
      "A page break must follow manuscript text before the division can be reviewed."
    );
  }

  return {
    ok: true,
    content,
    descriptors,
    forcedBoundaries,
    blockEndBoundaries,
    words,
    wordCount: wordCursor,
    sourceText: plainTextFromManuscriptContent(content),
  };
}

function boundaryDescriptor(layout, boundary) {
  if (boundary <= 0 || boundary >= layout.wordCount) return null;
  return (
    layout.descriptors.find(
      (descriptor) =>
        !descriptor.pageBreak &&
        descriptor.wordStart < boundary &&
        descriptor.wordEnd > boundary
    ) || null
  );
}

function boundaryIsAllowed(layout, boundary) {
  if (layout.blockEndBoundaries.has(boundary)) return true;
  return Boolean(boundaryDescriptor(layout, boundary)?.splittable);
}

function farthestSectionRanges(layout, sectionStart, sectionEnd, wordLimit) {
  const ranges = [];
  let start = sectionStart;

  while (start < sectionEnd) {
    const maximum = Math.min(sectionEnd, start + wordLimit);
    let boundary = maximum;
    while (boundary > start && boundary < sectionEnd) {
      if (boundaryIsAllowed(layout, boundary)) break;
      boundary -= 1;
    }
    if (boundary <= start) {
      return failure(
        "unsplittable_block_oversized",
        "A heading or list item is too long to fit safely on one physical page."
      );
    }
    ranges.push({ start, end: boundary });
    start = boundary;
  }

  return { ok: true, ranges };
}

function balancedSectionRanges(layout, sectionStart, sectionEnd, wordLimit) {
  const fallback = farthestSectionRanges(
    layout,
    sectionStart,
    sectionEnd,
    wordLimit
  );
  if (!fallback.ok || fallback.ranges.length <= 1) return fallback;

  const segmentCount = fallback.ranges.length;
  const memo = new Map();

  function search(start, remainingSegments) {
    const key = `${start}:${remainingSegments}`;
    if (memo.has(key)) return memo.get(key);

    if (remainingSegments === 1) {
      const result =
        sectionEnd - start <= wordLimit
          ? [{ start, end: sectionEnd }]
          : null;
      memo.set(key, result);
      return result;
    }

    const remainingWords = sectionEnd - start;
    const minimumBoundary = Math.max(
      start + 1,
      sectionEnd - wordLimit * (remainingSegments - 1)
    );
    const maximumBoundary = Math.min(
      start + wordLimit,
      sectionEnd - (remainingSegments - 1)
    );
    const target = start + Math.ceil(remainingWords / remainingSegments);
    const candidates = [];

    for (let boundary = minimumBoundary; boundary <= maximumBoundary; boundary += 1) {
      if (boundary >= sectionEnd || !boundaryIsAllowed(layout, boundary)) continue;
      candidates.push({
        boundary,
        wholeBlock: layout.blockEndBoundaries.has(boundary),
        distance: Math.abs(boundary - target),
      });
    }

    candidates.sort((first, second) => {
      if (first.wholeBlock !== second.wholeBlock) return first.wholeBlock ? -1 : 1;
      if (first.distance !== second.distance) return first.distance - second.distance;
      return second.boundary - first.boundary;
    });

    for (const candidate of candidates) {
      const rest = search(candidate.boundary, remainingSegments - 1);
      if (rest) {
        const result = [{ start, end: candidate.boundary }, ...rest];
        memo.set(key, result);
        return result;
      }
    }

    memo.set(key, null);
    return null;
  }

  return {
    ok: true,
    ranges: search(sectionStart, segmentCount) || fallback.ranges,
  };
}

function suggestedRanges(layout, wordLimit) {
  const forced = Array.from(layout.forcedBoundaries)
    .filter((boundary) => boundary > 0 && boundary < layout.wordCount)
    .sort((first, second) => first - second);
  const sectionEnds = [...forced, layout.wordCount];
  const ranges = [];
  let sectionStart = 0;

  for (const sectionEnd of sectionEnds) {
    if (sectionEnd <= sectionStart) {
      return failure(
        "unsupported_page_break_position",
        "Consecutive or empty page breaks cannot be divided safely."
      );
    }
    const section = balancedSectionRanges(
      layout,
      sectionStart,
      sectionEnd,
      wordLimit
    );
    if (!section.ok) return section;
    ranges.push(...section.ranges);
    sectionStart = sectionEnd;
  }

  return { ok: true, ranges };
}

function sliceRuns(runs, startCharacter, endCharacter) {
  const fragments = [];
  let cursor = 0;

  safeArray(runs).forEach((run) => {
    const text = typeof run?.text === "string" ? run.text : "";
    const runStart = cursor;
    const runEnd = runStart + text.length;
    cursor = runEnd;
    const overlapStart = Math.max(startCharacter, runStart);
    const overlapEnd = Math.min(endCharacter, runEnd);
    if (overlapStart >= overlapEnd) return;

    const fragmentText = text.slice(overlapStart - runStart, overlapEnd - runStart);
    if (!fragmentText) return;
    fragments.push({
      text: fragmentText,
      bold: Boolean(run.bold),
      italic: Boolean(run.italic),
      underline: Boolean(run.underline),
      ...(typeof run.fontFamily === "string" && run.fontFamily
        ? { fontFamily: run.fontFamily }
        : {}),
      ...(Number.isInteger(run.fontSize) && run.fontSize > 0
        ? { fontSize: run.fontSize }
        : {}),
    });
  });

  return fragments;
}

function splitBlock(descriptor, localStart, localEnd, fragmentNumber) {
  if (localStart === 0 && localEnd === descriptor.wordCount) {
    return {
      ...descriptor.block,
      runs: descriptor.block.runs.map((run) => ({ ...run })),
    };
  }
  if (!descriptor.splittable) return null;

  const startMatch =
    descriptor.matches[
      localStart
    ];

  const endMatch =
    descriptor.matches[
      localEnd - 1
    ];

  if (
    !startMatch ||
    !endMatch
  ) {
    return null;
  }

  const startCharacter =
    localStart === 0
      ? 0
      : startMatch.index;

  const nextMatch =
    descriptor.matches[
      localEnd
    ];

  const endCharacter =
    localEnd ===
      descriptor.wordCount
      ? descriptor.text.length
      : nextMatch?.index;

  if (
    !Number.isInteger(
      endCharacter
    )
  ) {
    return null;
  }

  return {
    ...descriptor.block,
    id:
      fragmentNumber === 1
        ? descriptor.block.id
        : continuationBlockId(descriptor.block.id, fragmentNumber),
    runs: sliceRuns(
      descriptor.block.runs,
      startCharacter,
      endCharacter
    ),
  };
}

function descriptorBelongsToRange(descriptor, range) {
  const position = descriptor.wordStart;
  if (position === 0) return range.start === 0;
  return position > range.start && position <= range.end;
}

function materializeRanges(layout, ranges) {
  const fragmentCounts = new Map();
  const segments = [];

  for (const range of ranges) {
    const blocks = [];

    for (const descriptor of layout.descriptors) {
      if (descriptor.wordCount === 0) {
        if (descriptorBelongsToRange(descriptor, range)) {
          blocks.push({ ...descriptor.block });
        }
        continue;
      }

      const overlapStart = Math.max(range.start, descriptor.wordStart);
      const overlapEnd = Math.min(range.end, descriptor.wordEnd);
      if (overlapStart >= overlapEnd) continue;

      const localStart = overlapStart - descriptor.wordStart;
      const localEnd = overlapEnd - descriptor.wordStart;
      const fragmentNumber = (fragmentCounts.get(descriptor.blockIndex) || 0) + 1;
      const block = splitBlock(descriptor, localStart, localEnd, fragmentNumber);
      if (!block) {
        return failure(
          "unsupported_block_split",
          "The proposed boundary would split a heading or list item."
        );
      }
      fragmentCounts.set(descriptor.blockIndex, fragmentNumber);
      blocks.push(block);
    }

    const normalized = normalizeManuscriptContent({
      version: MANUSCRIPT_CONTENT_VERSION,
      blocks,
    });
    if (!normalized.ok || !normalized.content) {
      return failure(
        "divided_content_invalid",
        normalized.errors[0] || "A proposed structured page is invalid."
      );
    }

    const manuscriptText = plainTextFromManuscriptContent(normalized.content);
    segments.push({
      manuscriptContent: normalized.content,
      manuscriptText,
      displayText: manuscriptText.replace(/\f/g, "").trim(),
      wordCount: wordsFromText(manuscriptText.replace(/\f/g, " ")).length,
    });
  }

  return { ok: true, segments };
}

function validateRanges(layout, ranges) {
  const mandatory = Array.from(layout.forcedBoundaries).filter(
    (boundary) => boundary > 0 && boundary < layout.wordCount
  );
  const boundaries = new Set(ranges.slice(0, -1).map((range) => range.end));

  if (mandatory.some((boundary) => !boundaries.has(boundary))) {
    return failure(
      "page_break_boundary_changed",
      "An existing page break must remain a physical-page boundary."
    );
  }

  const invalidBoundary = ranges
    .slice(0, -1)
    .map((range) => range.end)
    .find((boundary) => !boundaryIsAllowed(layout, boundary));
  if (Number.isInteger(invalidBoundary)) {
    return failure(
      "unsupported_block_split",
      "The proposed boundary would split a heading or list item."
    );
  }

  return { ok: true };
}

function resultFromRanges(layout, ranges) {
  const validated = validateRanges(layout, ranges);
  if (!validated.ok) return validated;
  const materialized = materializeRanges(layout, ranges);
  if (!materialized.ok) return materialized;

  return {
    ok: true,
    sourceText: layout.sourceText,
    sourceWordCount: layout.wordCount,
    ranges,
    segments: materialized.segments,
    summary: {
      manuscriptTextPreserved: true,
      manuscriptStructurePreserved: true,
      formattingPreserved: true,
      pageBreaksPreserved: true,
      sourceContentMode: "structured",
    },
  };
}

function validateWordLimit(wordLimit) {
  const value = Number(wordLimit);
  return Number.isInteger(value) && value > 0 ? value : null;
}

function oversizedUnsplittable(layout, wordLimit) {
  return layout.descriptors.find(
    (descriptor) => descriptor.unsplittable && descriptor.wordCount > wordLimit
  );
}

export function suggestChildrensBookStructuredManuscriptDivision({
  manuscriptContent,
  wordLimit,
} = {}) {
  const limit = validateWordLimit(wordLimit);
  if (!limit) {
    return failure(
      "invalid_word_limit",
      "A positive physical-page word limit is required."
    );
  }

  const layout = sourceLayout(manuscriptContent);
  if (!layout.ok) return layout;
  if (!layout.wordCount) {
    return failure(
      "source_manuscript_empty",
      "The manuscript unit has no text to divide."
    );
  }
  if (oversizedUnsplittable(layout, limit)) {
    return failure(
      "unsplittable_block_oversized",
      "A heading or list item is too long to fit safely on one physical page."
    );
  }

  const suggested = suggestedRanges(layout, limit);
  return suggested.ok ? resultFromRanges(layout, suggested.ranges) : suggested;
}

export function buildChildrensBookStructuredManuscriptDivision({
  manuscriptContent,
  segments,
  wordLimit,
} = {}) {
  const limit = validateWordLimit(wordLimit);
  if (!limit) {
    return failure(
      "invalid_word_limit",
      "A positive physical-page word limit is required."
    );
  }
  if (!Array.isArray(segments) || segments.length < 1) {
    return failure(
      "insufficient_segments",
      "At least one proposed physical page is required."
    );
  }

  const layout = sourceLayout(manuscriptContent);
  if (!layout.ok) return layout;
  if (!layout.wordCount) {
    return failure(
      "source_manuscript_empty",
      "The manuscript unit has no text to divide."
    );
  }

  const segmentWords = segments.map(wordsFromText);
  if (segmentWords.some((words) => words.length === 0)) {
    return failure(
      "empty_segment",
      "Every proposed physical page must contain manuscript text."
    );
  }

  const reconstructed = segmentWords.flat();
  if (
    reconstructed.length !== layout.words.length ||
    reconstructed.some((word, index) => word !== layout.words[index])
  ) {
    return failure(
      "manuscript_text_changed",
      "The proposed division does not preserve the complete manuscript text in its original order."
    );
  }
  if (oversizedUnsplittable(layout, limit)) {
    return failure(
      "unsplittable_block_oversized",
      "A heading or list item is too long to fit safely on one physical page."
    );
  }

  const ranges = [];
  let start = 0;
  segmentWords.forEach((words) => {
    const end = start + words.length;
    ranges.push({ start, end });
    start = end;
  });
  return resultFromRanges(layout, ranges);
}

/*
 * Pure variable-capacity structured manuscript division.
 *
 * This is deliberately separate from the existing uniform-word-limit
 * suggestion above. Existing callers therefore retain their established
 * behaviour.
 *
 * Each supplied word limit represents one already-existing physical text
 * slot, in order. The complete manuscript must fit inside those capacities.
 *
 * Boundaries remain subject to the same structured-manuscript protections
 * as ordinary division:
 * - explicit page breaks remain boundaries;
 * - headings and list items are not split;
 * - paragraph continuations retain structured identity;
 * - formatting and writer whitespace are materialized from the source
 *   structured content rather than reconstructed from plain text.
 *
 * This function does not create Book Structure units, persist anything or
 * assign illustration semantics. It is only a structured division primitive
 * for callers that already know the capacity of each destination page.
 */
function validateVariableWordLimits(
  wordLimits
) {
  if (
    !Array.isArray(
      wordLimits
    ) ||
    wordLimits.length < 1
  ) {
    return null;
  }

  const limits =
    wordLimits.map(
      (value) =>
        validateWordLimit(
          value
        )
    );

  return limits.every(
    (value) =>
      Number.isInteger(
        value
      ) &&
      value > 0
  )
    ? limits
    : null;
}


function nextForcedBoundaryAfter(
  layout,
  start
) {
  return (
    Array.from(
      layout.forcedBoundaries
    )
      .filter(
        (boundary) =>
          boundary > start &&
          boundary <
            layout.wordCount
      )
      .sort(
        (
          first,
          second
        ) =>
          first - second
      )[0] ?? null
  );
}


function variableCapacityRanges(
  layout,
  wordLimits
) {
  const ranges = [];

  let start = 0;

  for (
    const limit of wordLimits
  ) {
    if (
      start >=
      layout.wordCount
    ) {
      break;
    }

    const nextForcedBoundary =
      nextForcedBoundaryAfter(
        layout,
        start
      );

    const capacityEnd =
      Math.min(
        layout.wordCount,
        start + limit
      );

    const forcedBoundaryFits =
      Number.isInteger(
        nextForcedBoundary
      ) &&
      nextForcedBoundary <=
        capacityEnd;

    let boundary =
      forcedBoundaryFits
        ? nextForcedBoundary
        : capacityEnd;

    if (
      forcedBoundaryFits &&
      !boundaryIsAllowed(
        layout,
        boundary
      )
    ) {
      return failure(
        "unsupported_page_break_position",
        "An existing page break cannot be represented safely inside the supplied physical-page capacities."
      );
    }

    if (
      !forcedBoundaryFits
    ) {
      while (
        boundary > start &&
        boundary <
          layout.wordCount &&
        !boundaryIsAllowed(
          layout,
          boundary
        )
      ) {
        boundary -= 1;
      }
    }

    if (
      boundary <= start
    ) {
      return failure(
        "unsplittable_block_oversized",
        "A heading or list item is too long to fit safely inside the next physical-page capacity."
      );
    }

    ranges.push({
      start,
      end:
        boundary,
    });

    start =
      boundary;
  }

  if (
    start <
    layout.wordCount
  ) {
    return {
      ...failure(
        "variable_page_capacity_insufficient",
        "The supplied physical-page capacities cannot hold the complete manuscript text."
      ),

      remainingWordCount:
        layout.wordCount -
        start,

      consumedPageCount:
        ranges.length,
    };
  }

  return {
    ok: true,
    ranges,
  };
}


export function suggestChildrensBookStructuredManuscriptDivisionByWordLimits({
  manuscriptContent,
  wordLimits = [],
} = {}) {
  const limits =
    validateVariableWordLimits(
      wordLimits
    );

  if (!limits) {
    return failure(
      "invalid_word_limits",
      "One or more positive physical-page word limits are required."
    );
  }

  const layout =
    sourceLayout(
      manuscriptContent
    );

  if (!layout.ok) {
    return layout;
  }

  if (!layout.wordCount) {
    return failure(
      "source_manuscript_empty",
      "The manuscript unit has no text to divide."
    );
  }

  const suggested =
    variableCapacityRanges(
      layout,
      limits
    );

  if (!suggested.ok) {
    return suggested;
  }

  const result =
    resultFromRanges(
      layout,
      suggested.ranges
    );

  if (!result.ok) {
    return result;
  }

  return {
    ...result,

    appliedWordLimits:
      limits.slice(
        0,
        suggested
          .ranges
          .length
      ),
  };
}
